import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { ShiftPayment, ShiftWithRelations } from '@/types/models';
import { format } from "date-fns";
import { es } from "date-fns/locale";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const calculateTotalPaid = (payments: ShiftPayment[] | undefined | null): number => {
  if (!payments || payments.length === 0) {
    return 0;
  }

  return payments
    .filter(payment => payment.status === 'approved')
    .reduce((sum, payment) => sum + (payment.amount || 0), 0);
  //return payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
};

export const calculateTotalInReview = (payments: ShiftPayment[] | undefined | null): number => {
  if (!payments || payments.length === 0) {
    return 0;
  }

  return payments
    .filter(payment => payment.status === 'pending')
    .reduce((sum, payment) => sum + (payment.amount || 0), 0);
};

export const calculateShiftOutstandingBalance = (
  shift: ShiftWithRelations | null | undefined,
  totalPaidOverride?: number
): number => {
  if (!shift) {
    return 0;
  }

  // If the shift is active (not yet closed), the operator owes $0 to the admin at this point.
  if (shift.end_time === null || shift.end_time === undefined) {
    return 0;
  }

  // If the shift is closed, the operator owes the administrator's share.
  const amountOwedToAdmin = shift.amount_admin || 0;

  const totalPaidByOperator = typeof totalPaidOverride === 'number'
    ? totalPaidOverride
    : calculateTotalPaid(shift.payments);

  return totalPaidByOperator - amountOwedToAdmin;
};

export const formatDateFull = (dateString: string) => {
  return format(new Date(dateString), "dd/MM/yy hh:mm a", { locale: es });
};

export const formatDateYearMonth = (dateString: string) => {
  return format(new Date(dateString), "MMM yyyy", { locale: es });
};

export const formatDateDayMonth = (dateString: string) => {
  return format(new Date(dateString), "dd/MM hh:mm a", { locale: es });
};

export const formatDateDayMonthYear = (dateString: string) => {
  return format(new Date(dateString), "dd/MM/yyyy", { locale: es });
};

export const formatPhoneInput = (value: string): string | undefined => {
  if (value.startsWith('+') && !value.startsWith('+58')) {
    return undefined;
  }

  if (!value.startsWith('+58')) {
    return '+58';
  }

  return value;
};

export const getShiftPercentages = (
  shift: ShiftWithRelations | null | undefined,
  defaultOp: number = 25,
  defaultAdmin: number = 75
): { operatorPercentage: number; administratorPercentage: number } => {
  if (!shift) {
    return { operatorPercentage: defaultOp, administratorPercentage: defaultAdmin };
  }

  if (shift.total_collected > 0) {
    const opPercent = shift.operator_amount !== undefined && shift.operator_amount !== null
      ? Math.round((shift.operator_amount / shift.total_collected) * 100)
      : null;
    const adPercent = shift.amount_admin !== undefined && shift.amount_admin !== null
      ? Math.round((shift.amount_admin / shift.total_collected) * 100)
      : null;

    if (opPercent !== null && adPercent !== null) {
      return { operatorPercentage: opPercent, administratorPercentage: adPercent };
    }
  }

  if (shift.operator_percentage !== null && shift.operator_percentage !== undefined &&
    shift.administrator_percentage !== null && shift.administrator_percentage !== undefined) {
    return {
      operatorPercentage: shift.operator_percentage,
      administratorPercentage: shift.administrator_percentage
    };
  }

  const branch = shift.operator?.branch as any;
  if (branch?.operator_percentage !== null && branch?.operator_percentage !== undefined &&
    branch?.administrator_percentage !== null && branch?.administrator_percentage !== undefined) {
    return {
      operatorPercentage: branch.operator_percentage,
      administratorPercentage: branch.administrator_percentage
    };
  }

  return { operatorPercentage: defaultOp, administratorPercentage: defaultAdmin };
};